import { Component, Input, ElementRef, ViewChild, AfterViewChecked, OnDestroy, signal, OnChanges, SimpleChanges, effect, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SocketService, ChatMessage, GifData } from '../../../../services/socket';
import { GifPicker, GiphyGif } from '../gif-picker/gif-picker';

export interface SystemMessage {
  message: string;
  timestamp: string;
}

export interface CombinedMessage {
  type: 'chat' | 'system';
  timestamp: string;
  chatMessage?: ChatMessage;
  systemMessage?: SystemMessage;
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, GifPicker],
  templateUrl: './chat.html',
  styleUrl: './chat.css'
})
export class Chat implements AfterViewChecked, OnChanges, OnDestroy {
  @Input() leagueId!: number;
  @Input() isOpen = false;
  @Input() set systemMessages(value: SystemMessage[]) {
    this._systemMessages.set(value);
  }
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('messageInput') private messageInput!: ElementRef<HTMLInputElement>;

  newMessage = signal('');
  showDraftActivity = signal(true);
  showGifPicker = signal(false);
  private _systemMessages = signal<SystemMessage[]>([]);
  private shouldScroll = false;
  private lastMessageCount = 0;
  private viewportHandler: (() => void) | null = null;

  combinedMessages = computed<CombinedMessage[]>(() => {
    const chatMessages = this.socketService.chatMessages();
    const showActivity = this.showDraftActivity();
    const systemMessages = this._systemMessages();

    const combined: CombinedMessage[] = [];

    // Add chat messages
    for (const msg of chatMessages) {
      combined.push({
        type: 'chat',
        timestamp: msg.timestamp,
        chatMessage: msg
      });
    }

    // Add system messages if enabled
    if (showActivity) {
      for (const msg of systemMessages) {
        combined.push({
          type: 'system',
          timestamp: msg.timestamp,
          systemMessage: msg
        });
      }
    }

    // Sort by timestamp
    combined.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return combined;
  });

  constructor(public socketService: SocketService, private el: ElementRef) {
    // Auto-scroll when new messages arrive
    effect(() => {
      const messages = this.socketService.chatMessages();
      if (messages.length > 0) {
        this.shouldScroll = true;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      // Small delay to allow the drawer animation to start
      setTimeout(() => this.focusInput(), 100);
      this.attachViewportListener();
    } else if (changes['isOpen']?.currentValue === false) {
      this.detachViewportListener();
    }
    // Auto-scroll when new system messages arrive
    if (changes['systemMessages']) {
      const totalCount = this._systemMessages().length + this.socketService.chatMessages().length;
      if (totalCount > this.lastMessageCount) {
        this.shouldScroll = true;
        this.lastMessageCount = totalCount;
      }
    }
  }

  ngOnDestroy(): void {
    this.detachViewportListener();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  toggleDraftActivity(): void {
    this.showDraftActivity.update(v => !v);
  }

  sendMessage(): void {
    const message = this.newMessage().trim();
    if (!message || !this.leagueId) return;

    this.socketService.sendChatMessage(this.leagueId, message);
    this.newMessage.set('');
    this.shouldScroll = true;
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  toggleGifPicker(): void {
    this.showGifPicker.update(v => !v);
  }

  onGifSelected(gif: GiphyGif): void {
    if (!this.leagueId) return;

    const gifData: GifData = {
      id: gif.id,
      url: gif.original.url,
      width: gif.original.width,
      height: gif.original.height,
      title: gif.title
    };

    this.socketService.sendGifMessage(this.leagueId, gifData);
    this.showGifPicker.set(false);
    this.shouldScroll = true;
  }

  private attachViewportListener(): void {
    if (this.viewportHandler || !window.visualViewport) return;

    const drawer = this.el.nativeElement.closest('.chat-drawer') as HTMLElement | null;
    if (!drawer) return;

    this.viewportHandler = () => {
      const vv = window.visualViewport!;
      drawer.style.height = `${vv.height}px`;
      drawer.style.top = `${vv.offsetTop}px`;
    };

    window.visualViewport.addEventListener('resize', this.viewportHandler);
    window.visualViewport.addEventListener('scroll', this.viewportHandler);
  }

  private detachViewportListener(): void {
    if (!this.viewportHandler || !window.visualViewport) return;

    window.visualViewport.removeEventListener('resize', this.viewportHandler);
    window.visualViewport.removeEventListener('scroll', this.viewportHandler);
    this.viewportHandler = null;

    // Reset drawer to CSS default
    const drawer = this.el.nativeElement.closest('.chat-drawer') as HTMLElement | null;
    if (drawer) {
      drawer.style.height = '';
      drawer.style.top = '';
    }
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      const el = this.messagesContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  focusInput(): void {
    this.messageInput?.nativeElement?.focus();
  }

  trackByMessage(index: number, msg: CombinedMessage): string {
    if (msg.type === 'chat' && msg.chatMessage) {
      return `chat-${msg.chatMessage.id}`;
    }
    return `system-${msg.timestamp}`;
  }

  formatTime(timestamp: string): string {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}
